import React, { useState, useEffect, useCallback } from 'react';
import { reportService } from '../../services/reportService';
import DateRangeFilter from './DateRangeFilter';
import { Download, AlertTriangle } from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip
} from 'recharts';
import SortableHeader, { useSort } from './SortableHeader';
import ChartContainer from './ChartContainer';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const COLORS = ['#CA8A04', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#F59E0B', '#6366F1', '#EC4899'];

const CaseStatusReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('status');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { group_by: groupBy };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await reportService.getCaseStatus(params);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching case status:', err);
      setError('Failed to load Case Status report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, groupBy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { sortConfig, requestSort, sortItems } = useSort('case_count', 'desc');

  const handleDateChange = ({ start_date, end_date }) => {
    setStartDate(start_date);
    setEndDate(end_date);
  };

  const handleExportCSV = () => {
    const params = { group_by: groupBy };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    reportService.downloadCSV(reportService.getCaseStatus, params, 'case_status_report.csv');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);

  const chartData = items.map(item => ({
    name: item.group_key,
    value: item.case_count,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Case Status Summary</h2>
        <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <DateRangeFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} />
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Group by:</span>
          {['status', 'case_type', 'attorney'].map((g) => (
            <button
              key={g}
              onClick={() => setGroupBy(g)}
              className={`px-3 py-1 text-sm rounded font-medium transition-colors ${
                groupBy === g ? 'bg-yellow-100 text-yellow-800' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {g === 'case_type' ? 'Case Type' : g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Cases</p>
          <p className="text-lg font-bold text-blue-700">{summary.total_cases || 0}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Active</p>
          <p className="text-lg font-bold text-green-700">{summary.active_cases || 0}</p>
        </div>
        <div className="bg-gray-100 rounded-lg p-4">
          <p className="text-xs text-gray-500">Closed</p>
          <p className="text-lg font-bold text-gray-700">{summary.closed_cases || 0}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Avg Days Open</p>
          <p className="text-lg font-bold text-yellow-700">{summary.avg_days_open != null ? Math.round(summary.avg_days_open) : '-'}</p>
        </div>
      </div>

      {/* Chart + Table layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pie Chart */}
        <div className="bg-white rounded-lg shadow p-4 lg:col-span-1">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Distribution</h3>
          {chartData.length > 0 ? (
            <>
              <ChartContainer className="h-56">
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      innerRadius={42}
                      paddingAngle={2}
                    >
                      {chartData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} cases`, name]} />
                  </PieChart>
              </ChartContainer>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 max-h-40 overflow-y-auto">
                {chartData.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                    {item.name} ({item.value})
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-56 text-gray-400 text-sm">No case data</div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden lg:col-span-2">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader label={groupBy === 'case_type' ? 'Case Type' : groupBy.charAt(0).toUpperCase() + groupBy.slice(1)} sortKey="group_key" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Cases" sortKey="case_count" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader label="Avg Days Open" sortKey="avg_days_open" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader label="Est. Value" sortKey="total_estimated_value" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader label="Overdue Tasks" sortKey="cases_with_overdue_tasks" sortConfig={sortConfig} onSort={requestSort} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedItems.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No cases found</td></tr>
                ) : sortedItems.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      {item.group_key}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{item.case_count}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{item.avg_days_open != null ? Math.round(item.avg_days_open) : '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.total_estimated_value)}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      {item.cases_with_overdue_tasks > 0 ? (
                        <span className="text-red-600 font-medium">{item.cases_with_overdue_tasks}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStatusReport;
